import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';

import {
  DrawerContentComponent,
  DrawerTitleComponent,
  Icon,
  PrimengDrawerDirective,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
  ToastService,
} from '@web/shared/ui';
import { PublicSubscriptionListCustomCellTemplateComponent } from '../../components/public-subscription-list-custom-cell-template/public-subscription-list-custom-cell-template.component';
import {
  PublicSubscriptionCollectionFilter,
  PublicSubscriptionCollectionItem,
  PublicSubscriptionDataSourceService,
  SubscriptionNumber,
} from '@web/shared/module/shop';
import { HttpErrorService, Route, RouteParam } from '@web/common';
import { Observable } from 'rxjs';
import { PublicSubscriptionListItemAction } from '../../enums/public-subscription-list-item-action';
import { HttpErrorResponse } from '@angular/common/http';
import { isNotNil, Nillable } from '@web/utils';
import { PublicSubscriptionBillingAddressEditComponent } from '../public-subscription-billing-address-edit/public-subscription-billing-address-edit.component';
import { Drawer } from 'primeng/drawer';
import { PublicSubscriptionShippingAddressEditComponent } from '../public-subscription-shipping-address-edit/public-subscription-shipping-address-edit.component';
import { PublicSubscriptionEditComponent } from '../public-subscription-edit/public-subscription-edit.component';

@Component({
  selector: 'app-public-subscription-list',
  imports: [
    TableComponent,
    PublicSubscriptionListCustomCellTemplateComponent,
    PublicSubscriptionBillingAddressEditComponent,
    PrimengDrawerDirective,
    Drawer,
    DrawerContentComponent,
    DrawerTitleComponent,
    PublicSubscriptionShippingAddressEditComponent,
    PublicSubscriptionEditComponent,
  ],
  templateUrl: './public-subscription-list.component.html',
  styleUrl: './public-subscription-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableService],
})
export class PublicSubscriptionListComponent implements OnInit {
  /* prettier-ignore */
  protected readonly selectedSubscriptionNumber = signal<Nillable<SubscriptionNumber>>(null);
  /* prettier-ignore */
  protected readonly billingAddressEditVisibility = this.billingAddressEditVisibilitySignal();
  /* prettier-ignore */
  protected readonly shippingAddressEditVisibility = this.shippingAddressEditVisibilitySignal();
  /* prettier-ignore */
  protected readonly subscriptionEditVisibility = this.subscriptionEditVisibilitySignal();

  /* prettier-ignore */
  protected config!: TableConfig<PublicSubscriptionCollectionItem, PublicSubscriptionCollectionFilter, PublicSubscriptionListItemAction>;

  private readonly billingAddressEdit = signal(false);
  private readonly subscriptionEdit = signal(false);
  private readonly shippingAddressEdit = signal(false);
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<PublicSubscriptionCollectionItem, PublicSubscriptionCollectionFilter, PublicSubscriptionListItemAction>).create();
  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(PublicSubscriptionDataSourceService);
  private readonly table = inject(TableService);
  private readonly toast = inject(ToastService);
  private readonly httpError = inject(HttpErrorService);

  public readonly customBillingAddressEditDialogCloseEvent = () => {
    this.clearSelectedSubscriptionNumber();
    this.billingAddressEdit.set(false);
  };

  public readonly customEditDialogCloseEvent = () => {
    this.clearSelectedSubscriptionNumber();
    this.subscriptionEdit.set(false);
  };

  public readonly customShippingAddressEditDialogCloseEvent = () => {
    this.clearSelectedSubscriptionNumber();
    this.shippingAddressEdit.set(false);
  };

  public ngOnInit(): void {
    this.buildTable();
  }

  public handleBillingAddressCancelEvent(): void {
    this.clearSelectedSubscriptionNumber();
    this.billingAddressEdit.set(false);
  }

  public handleShippingAddressCancelEvent(): void {
    this.clearSelectedSubscriptionNumber();
    this.shippingAddressEdit.set(false);
  }

  public handleBillingAddressSubmitEvent(): void {
    this.clearSelectedSubscriptionNumber();
    this.billingAddressEdit.set(false);
    this.refresh();
  }

  public handleSubscriptionEditCancelEvent(): void {
    this.clearSelectedSubscriptionNumber();
    this.subscriptionEdit.set(false);
  }

  public handleSubscriptionEditSubmitEvent(): void {
    this.clearSelectedSubscriptionNumber();
    this.subscriptionEdit.set(false);
    this.refresh();
  }

  public handleShippingAddressSubmitEvent(): void {
    this.clearSelectedSubscriptionNumber();
    this.shippingAddressEdit.set(false);
    this.refresh();
  }

  private clearSelectedSubscriptionNumber(): void {
    this.selectedSubscriptionNumber.set(null);
  }

  private billingAddressEditVisibilitySignal() {
    return computed(
      () =>
        isNotNil(this.selectedSubscriptionNumber()) &&
        this.billingAddressEdit(),
    );
  }

  private shippingAddressEditVisibilitySignal(): Signal<boolean> {
    return computed(
      () =>
        isNotNil(this.selectedSubscriptionNumber()) &&
        this.shippingAddressEdit(),
    );
  }

  private subscriptionEditVisibilitySignal(): Signal<boolean> {
    return computed(
      () =>
        isNotNil(this.selectedSubscriptionNumber()) && this.subscriptionEdit(),
    );
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .topScrollEnabled(false)
      .responsiveLayout('stack')
      .filtering(false)
      .filters((builder) => [builder.initText('number').build()])
      .itemActions((builder) => [
        builder
          .initMore(PublicSubscriptionListItemAction.More)
          .visibility((item) => this.visibilityForMoreItemAction(item))
          .children((builder) => [
            builder
              .initRouterLink(PublicSubscriptionListItemAction.Preview)
              .icon(Icon.Eye)
              .tooltip('Podgląd')
              .visibility((item) => item.canBeViewed)
              .routerLink(
                (provider) => (item) =>
                  provider.routerLink(Route.PL_AccountSubscriptionDetails, {
                    [RouteParam.Number]: item.number,
                  }),
              )
              .build(),

            builder
              .initCommand(PublicSubscriptionListItemAction.Edit)
              .icon(Icon.Pencil)
              .tooltip('Edytuj')
              .visibility((item) => item.canBeUpdated)
              .command((item) => this.editSubscription(item))
              .build(),

            builder
              .initCommand(PublicSubscriptionListItemAction.BillingAddressEdit)
              .icon(Icon.CashCoin)
              .tooltip('Edytuj adres rozliczeniowy')
              .visibility((item) => item.canBillingAddressBeUpdated)
              .command((item) => this.editBillingAddress(item))
              .build(),

            builder
              .initCommand(PublicSubscriptionListItemAction.ShippingAddressEdit)
              .icon(Icon.Truck)
              .tooltip('Edytuj adres dostawy')
              .visibility((item) => item.canShippingAddressBeUpdated)
              .command((item) => this.editShippingAddress(item))
              .build(),

            builder
              .initCommand(PublicSubscriptionListItemAction.Suspend)
              .icon(Icon.PauseCircle)
              .tooltip('Wstrzymaj')
              .visibility((item) => item.canBeSuspended)
              .command((model) => this.suspend(model))
              .build(),

            builder
              .initCommand(PublicSubscriptionListItemAction.Resume)
              .icon(Icon.PlayCircle)
              .tooltip('Wznów')
              .visibility((item) => item.canBeResumed)
              .command((model) => this.resume(model))
              .build(),

            builder
              .initCommand(PublicSubscriptionListItemAction.Cancel)
              .icon(Icon.XCircle)
              .tooltip('Anuluj')
              .severity('danger')
              .visibility((item) => item.canBeCancelled)
              .command((model) => this.cancel(model))
              .build(),
          ])
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initOrdinal()
          .build(),

        builder //
          .initCustom('number')
          .label('Numer')
          .width('8rem')
          .sortKey('number')
          .build(),

        builder //
          .initCustom('status')
          .width('10rem')
          .label('Status')
          .sortKey('status')
          .build(),

        builder //
          .initCustom('totalPrice')
          .label('Kwota')
          .width('12rem')
          .sortKey('totalPrice')
          .build(),

        builder //
          .initDate('nextPaymentDate')
          .label('Następna płatność')
          .sortKey('nextPaymentDate')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<PublicSubscriptionCollectionFilter>,
        ): Observable<TableDataPayload<PublicSubscriptionCollectionItem>> =>
          this.subscriptionDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private visibilityForMoreItemAction(
    item: PublicSubscriptionCollectionItem,
  ): boolean {
    return (
      item.canBillingAddressBeUpdated ||
      item.canBeCancelled ||
      item.canBeViewed ||
      item.canBeSuspended ||
      item.canBeResumed
    );
  }

  private cancel(model: PublicSubscriptionCollectionItem): void {
    this.subscriptionDataSource
      .confirmAndCancelByNumber(model.number)
      .subscribe({
        next: () => this.refresh(),
        error: (error) => this.showFailureToast(error),
      });
  }

  private suspend(model: PublicSubscriptionCollectionItem): void {
    this.subscriptionDataSource
      .confirmAndSuspendByNumber(model.number)
      .subscribe({
        next: () => this.refresh(),
        error: (error) => this.showFailureToast(error),
      });
  }

  private resume(model: PublicSubscriptionCollectionItem): void {
    this.subscriptionDataSource
      .confirmAndResumeByNumber(model.number)
      .subscribe({
        next: () => this.refresh(),
        error: (error) => this.showFailureToast(error),
      });
  }

  private showFailureToast(error: HttpErrorResponse): void {
    this.toast.show((builder) =>
      builder
        .initError(this.httpError.extract(error).multiLineMessages())
        .build(),
    );
  }

  private editBillingAddress(item: PublicSubscriptionCollectionItem): void {
    this.selectedSubscriptionNumber.set(item.number);
    this.billingAddressEdit.set(true);
  }

  private editSubscription(item: PublicSubscriptionCollectionItem): void {
    this.selectedSubscriptionNumber.set(item.number);
    this.subscriptionEdit.set(true);
  }

  private editShippingAddress(item: PublicSubscriptionCollectionItem): void {
    this.selectedSubscriptionNumber.set(item.number);
    this.shippingAddressEdit.set(true);
  }

  private refresh(): void {
    this.table.refresh();
  }
}
